import { useState, useEffect, useRef } from "react";
import {
    FaCalculator,
    FaMoneyBillWave,
    FaPercent,
    FaCalendarAlt,
    FaPlusCircle,
    FaCheckCircle,
    FaExclamationCircle,
    FaDollarSign,
    FaEuroSign,
    FaPoundSign,
    FaYenSign,
    FaRupeeSign,
} from "react-icons/fa";
import Chart from "chart.js/auto";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { useTheme } from "../../context/ThemeContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalculator } from "@fortawesome/free-solid-svg-icons";

export default function AdvancedLoanCalculator() {
    // State for currency
    const [currency, setCurrency] = useState("USD");
    const { darkMode } = useTheme();
    const currencySymbols = {
        USD: "$",
        EUR: "€",
        GBP: "£",
        JPY: "¥",
        INR: "₹",
        PKR: "₨",
    };

    // State for form inputs
    const [formData, setFormData] = useState({
        amount: "",
        interest: "",
        term: "",
        extra: "",
    });

    // State for results
    const [results, setResults] = useState({
        monthlyPayment: "-",
        totalPayment: "-",
        totalInterest: "-",
        payoffTime: "-",
        amortization: [],
    });

    // State for active tab
    const [activeTab, setActiveTab] = useState("summary");

    // State for notification
    const [notification, setNotification] = useState({
        show: false,
        message: "",
        type: "success",
    });

    // Refs
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    // Initialize chart when component mounts
    useEffect(() => {
        return () => {
            // Cleanup chart when component unmounts
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, []);

    // Format currency
    const formatCurrency = (amount) => {
        if (isNaN(amount)) return "-";
        return (
            currencySymbols[currency] +
            amount.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        );
    };

    // Show notification
    const showNotification = (message, type = "success") => {
        setNotification({ show: true, message, type });
        setTimeout(() => {
            setNotification({ show: false, message: "", type: "success" });
        }, 2000);
    };

    // Calculate loan
    const calculateLoan = () => {
        const amount = parseFloat(formData.amount);
        const interest = parseFloat(formData.interest);
        const term = parseFloat(formData.term);
        const extra = parseFloat(formData.extra) || 0;

        // Validate inputs
        if (isNaN(amount)) {
            showNotification("Please enter a valid loan amount", "error");
            return;
        }
        if (isNaN(interest)) {
            showNotification("Please enter a valid interest rate", "error");
            return;
        }
        if (isNaN(term)) {
            showNotification("Please enter a valid loan term", "error");
            return;
        }

        // Calculate monthly payment
        const monthlyRate = interest / 100 / 12;
        const months = term * 12;
        const monthlyPayment =
            (amount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
        const totalPayment = (monthlyPayment + extra) * months;
        const totalInterest = totalPayment - amount;

        // Generate amortization schedule
        let balance = amount;
        const amortization = [];

        for (let month = 1; month <= months && balance > 0; month++) {
            const interestPayment = balance * monthlyRate;
            let principalPayment = monthlyPayment - interestPayment;
            const totalPayment = monthlyPayment + extra;

            // Adjust final payment if needed
            if (month === months || balance - (principalPayment + extra) < 0) {
                principalPayment = balance - extra;
                if (principalPayment < 0) principalPayment = 0;
            }

            balance -= principalPayment + extra;

            amortization.push({
                month,
                payment: totalPayment,
                principal: principalPayment + extra,
                interest: interestPayment,
                balance: balance > 0 ? balance : 0,
            });

            if (balance <= 0) break;
        }

        // Update results
        setResults({
            monthlyPayment: formatCurrency(monthlyPayment + extra),
            totalPayment: formatCurrency(totalPayment),
            totalInterest: formatCurrency(totalInterest),
            payoffTime: `${Math.ceil(amortization.length)} months (${(
                amortization.length / 12
            ).toFixed(1)} years)`,
            amortization,
        });

        // Render chart if chart tab is active
        if (activeTab === "chart") {
            renderChart(amount, totalInterest);
        }

        // Show success notification
        showNotification("Loan calculation complete!");
    };

    // Render chart
    const renderChart = (principal, interest) => {
        if (!chartRef.current) return;

        const ctx = chartRef.current.getContext("2d");
        if (!ctx) return;

        // Destroy previous chart if exists
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        try {
            chartInstance.current = new Chart(ctx, {
                type: "doughnut",
                data: {
                    labels: ["Principal", "Interest"],
                    datasets: [
                        {
                            data: [principal, interest],
                            backgroundColor: ["#10b981", "#f59e0b"],
                            borderWidth: 0,
                            hoverOffset: 10,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: "bottom",
                            labels: {
                                color: "#1e293b",
                                font: {
                                    family: "Poppins",
                                    size: 14,
                                },
                                padding: 20,
                            },
                        },
                        tooltip: {
                            callbacks: {
                                label: function (context) {
                                    return `${context.label}: ${formatCurrency(
                                        context.raw
                                    )}`;
                                },
                            },
                        },
                    },
                    cutout: "65%",
                    animation: {
                        animateScale: true,
                        animateRotate: true,
                    },
                },
            });
        } catch (error) {
            console.error("Chart error:", error);
        }
    };

    // Handle tab change
    const handleTabChange = (tab) => {
        setActiveTab(tab);

        // Render chart if switching to chart tab and we have results
        if (tab === "chart" && results.monthlyPayment !== "-") {
            const amount = parseFloat(formData.amount);
            const interest = parseFloat(
                results.totalInterest.replace(/[^0-9.-]+/g, "")
            );
            if (!isNaN(amount)) {
                renderChart(amount, interest);
            }
        }
    };

    // Handle input changes
    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [id]: value,
        }));
    };

    // Set currency
    const handleSetCurrency = (curr) => {
        setCurrency(curr);
        // Recalculate if we already have results
        if (results.monthlyPayment !== "-") {
            calculateLoan();
        }
    };

    return (
        <>
            <HeaderSection toolId="loanCalculator" />
            <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4">
                <div className="bg-white rounded-xl dark:bg-gray-800 shadow-md dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300">
                    {/* Gradient Header */}
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon
                                icon={faCalculator}
                                className="mr-3"
                            />
                            Loan Calculator
                        </h1>
                        <p className="opacity-90">
                            Calculate your loan payments with comprehensive
                            validation checks
                        </p>
                    </div>

                    {/* Notification */}
                    {notification.show && (
                        <div
                            className={`fixed bottom-5 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-full flex items-center gap-2 shadow-lg z-50 animate-fadeInOut ${
                                notification.type === "success"
                                    ? "bg-green-500 dark:bg-green-600"
                                    : "bg-red-500 dark:bg-red-600"
                            } text-white`}
                        >
                            {notification.type === "success" ? (
                                <FaCheckCircle className="text-lg" />
                            ) : (
                                <FaExclamationCircle className="text-lg" />
                            )}
                            <span className="text-sm font-medium">
                                {notification.message}
                            </span>
                        </div>
                    )}

                    {/* Main Calculator Card */}
                    <div className="p-6">
                        {/* Calculator Header */}
                        <header className="text-center mb-6">
                            <h1 className="text-2xl font-bold mb-2 text-gray-800 dark:text-gray-200">
                                Advanced Loan Calculator
                            </h1>
                            <div className="h-1 w-20 mx-auto bg-green-500 dark:bg-green-600 rounded-full"></div>
                        </header>

                        {/* Currency Selector */}
                        <section className="flex flex-wrap gap-2 mb-6">
                            {["USD", "EUR", "GBP", "JPY", "INR", "PKR"].map(
                                (curr) => (
                                    <button
                                        key={curr}
                                        onClick={() => handleSetCurrency(curr)}
                                        className={`px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all text-sm ${
                                            currency === curr
                                                ? "bg-green-100 dark:bg-green-900 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 font-medium"
                                                : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                                        }`}
                                    >
                                        {curr === "USD" && <FaDollarSign />}
                                        {curr === "EUR" && <FaEuroSign />}
                                        {curr === "GBP" && <FaPoundSign />}
                                        {curr === "JPY" && <FaYenSign />}
                                        {curr === "INR" && <FaRupeeSign />}
                                        {curr === "PKR" && <FaRupeeSign />}
                                        {curr}
                                    </button>
                                )
                            )}
                        </section>

                        {/* Input Grid */}
                        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            {/* Loan Amount */}
                            <div className="space-y-1">
                                <label
                                    htmlFor="amount"
                                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                >
                                    Loan Amount
                                </label>
                                <div className="relative">
                                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">
                                        <FaMoneyBillWave />
                                    </div>
                                    <input
                                        type="number"
                                        id="amount"
                                        value={formData.amount}
                                        onChange={handleChange}
                                        placeholder="e.g. 100000"
                                        min="1"
                                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors duration-300"
                                    />
                                </div>
                            </div>

                            {/* Interest Rate */}
                            <div className="space-y-1">
                                <label
                                    htmlFor="interest"
                                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                >
                                    Interest Rate (%)
                                </label>
                                <div className="relative">
                                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">
                                        <FaPercent />
                                    </div>
                                    <input
                                        type="number"
                                        id="interest"
                                        value={formData.interest}
                                        onChange={handleChange}
                                        placeholder="e.g. 5.5"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors duration-300"
                                    />
                                </div>
                            </div>

                            {/* Loan Term */}
                            <div className="space-y-1">
                                <label
                                    htmlFor="term"
                                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                >
                                    Loan Term (Years)
                                </label>
                                <div className="relative">
                                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">
                                        <FaCalendarAlt />
                                    </div>
                                    <input
                                        type="number"
                                        id="term"
                                        value={formData.term}
                                        onChange={handleChange}
                                        placeholder="e.g. 30"
                                        min="1"
                                        max="50"
                                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors duration-300"
                                    />
                                </div>
                            </div>

                            {/* Extra Payment */}
                            <div className="space-y-1">
                                <label
                                    htmlFor="extra"
                                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                >
                                    Extra Monthly Payment
                                </label>
                                <div className="relative">
                                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">
                                        <FaPlusCircle />
                                    </div>
                                    <input
                                        type="number"
                                        id="extra"
                                        value={formData.extra}
                                        onChange={handleChange}
                                        placeholder="e.g. 100"
                                        min="0"
                                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors duration-300"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* Calculate Button */}
                        <section className="mb-6">
                            <button
                                onClick={calculateLoan}
                                className="w-full py-3 px-4 rounded-lg font-medium text-white bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 shadow-md hover:shadow-lg transition-all duration-300 focus:ring-2 focus:ring-green-500 focus:outline-none flex items-center justify-center gap-2"
                            >
                                <FaCalculator />
                                Calculate Loan
                            </button>
                        </section>

                        {/* Tabs */}
                        <nav className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
                            <button
                                onClick={() => handleTabChange("summary")}
                                className={`px-4 py-2 font-medium text-sm transition-all ${
                                    activeTab === "summary"
                                        ? "text-green-600 dark:text-green-500 border-b-2 border-green-500 dark:border-green-500"
                                        : "text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300"
                                }`}
                            >
                                Summary
                            </button>
                            <button
                                onClick={() => handleTabChange("amortization")}
                                className={`px-4 py-2 font-medium text-sm transition-all ${
                                    activeTab === "amortization"
                                        ? "text-green-600 dark:text-green-500 border-b-2 border-green-500 dark:border-green-500"
                                        : "text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300"
                                }`}
                            >
                                Amortization
                            </button>
                            <button
                                onClick={() => handleTabChange("chart")}
                                className={`px-4 py-2 font-medium text-sm transition-all ${
                                    activeTab === "chart"
                                        ? "text-green-600 dark:text-green-500 border-b-2 border-green-500 dark:border-green-500"
                                        : "text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300"
                                }`}
                            >
                                Visualization
                            </button>
                        </nav>

                        {/* Tab Contents */}
                        <section>
                            {/* Summary Tab */}
                            {activeTab === "summary" && (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                    <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 shadow-sm">
                                        <h3 className="text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                                            Monthly Payment
                                        </h3>
                                        <p className="text-lg font-semibold text-green-600 dark:text-green-500">
                                            {results.monthlyPayment}
                                        </p>
                                    </div>
                                    <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 shadow-sm">
                                        <h3 className="text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                                            Total Payment
                                        </h3>
                                        <p className="text-lg font-semibold text-green-600 dark:text-green-500">
                                            {results.totalPayment}
                                        </p>
                                    </div>
                                    <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 shadow-sm">
                                        <h3 className="text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                                            Total Interest
                                        </h3>
                                        <p className="text-lg font-semibold text-green-600 dark:text-green-500">
                                            {results.totalInterest}
                                        </p>
                                    </div>
                                    <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 shadow-sm">
                                        <h3 className="text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                                            Payoff Time
                                        </h3>
                                        <p className="text-lg font-semibold text-green-600 dark:text-green-500">
                                            {results.payoffTime}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Amortization Tab */}
                            {activeTab === "amortization" && (
                                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 shadow-sm">
                                    <table className="w-full">
                                        <thead className="bg-gray-100 dark:bg-gray-800">
                                            <tr>
                                                <th className="py-3 px-4 text-left text-xs font-medium text-gray-700 dark:text-gray-300">
                                                    Month
                                                </th>
                                                <th className="py-3 px-4 text-left text-xs font-medium text-gray-700 dark:text-gray-300">
                                                    Payment
                                                </th>
                                                <th className="py-3 px-4 text-left text-xs font-medium text-gray-700 dark:text-gray-300">
                                                    Principal
                                                </th>
                                                <th className="py-3 px-4 text-left text-xs font-medium text-gray-700 dark:text-gray-300">
                                                    Interest
                                                </th>
                                                <th className="py-3 px-4 text-left text-xs font-medium text-gray-700 dark:text-gray-300">
                                                    Balance
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {results.amortization.length > 0 ? (
                                                results.amortization.map(
                                                    (row) => (
                                                        <tr
                                                            key={row.month}
                                                            className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-200"
                                                        >
                                                            <td className="py-3 px-4 text-sm text-gray-800 dark:text-gray-300">
                                                                {row.month}
                                                            </td>
                                                            <td className="py-3 px-4 text-sm text-gray-800 dark:text-gray-300">
                                                                {formatCurrency(
                                                                    row.payment
                                                                )}
                                                            </td>
                                                            <td className="py-3 px-4 text-sm text-gray-800 dark:text-gray-300">
                                                                {formatCurrency(
                                                                    row.principal
                                                                )}
                                                            </td>
                                                            <td className="py-3 px-4 text-sm text-gray-800 dark:text-gray-300">
                                                                {formatCurrency(
                                                                    row.interest
                                                                )}
                                                            </td>
                                                            <td className="py-3 px-4 text-sm text-gray-800 dark:text-gray-300">
                                                                {row.balance > 0
                                                                    ? formatCurrency(
                                                                          row.balance
                                                                      )
                                                                    : "Paid Off"}
                                                            </td>
                                                        </tr>
                                                    )
                                                )
                                            ) : (
                                                <tr className="border-t border-gray-200 dark:border-gray-700">
                                                    <td
                                                        colSpan="5"
                                                        className="py-4 text-center text-sm text-gray-500 dark:text-gray-400"
                                                    >
                                                        Calculate loan to see
                                                        amortization schedule
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Chart Tab */}
                            {activeTab === "chart" && (
                                <div className="relative h-72 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                                    <canvas
                                        ref={chartRef}
                                        style={{
                                            display:
                                                results.monthlyPayment !== "-"
                                                    ? "block"
                                                    : "none",
                                        }}
                                    ></canvas>
                                    {results.monthlyPayment === "-" && (
                                        <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">
                                            Calculate loan to see visualization
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>
                    </div>
                </div>
            </div>
            <BottomSection toolId="loanCalculator" />
        </>
    );
}
